import React, {
  ComponentProps,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { Command } from 'cmdk';
import * as Select from '@radix-ui/react-select';
import Icon from '../Icon';
import MenuText from './MenuText';
import { isExternalUrl } from '@/utils/helpers';
import SimpleTooltip from '../Tooltip/SimpleTooltip';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { MenuStyle } from './MenuStyle';
import LoadingDots from '../Buttons/LoadingDots';

export type MenuItemText = {
  label?: ReactNode;
  // Used outside the menu
  eyebrow?: string;
  // Used inside the menu, falling back to eyebrow
  shortEyebrow?: string;
  smallClassName?: string;
  valueClassName?: string;
};

export type MenuItemProps = MenuItemText & {
  disabled?: boolean;
  isLoading?: boolean;
  condensed?: boolean;
  visible?: boolean;
  icon?: ComponentProps<typeof Icon>['name'];
  key?: string | undefined;
  rightIcon?: ComponentProps<typeof Icon>['name'];
  tooltipText?: string;
} & (
    | {
        onClick: () => void;
        Element?: undefined;
        isHighlighted?: undefined;
      }
    | {
        to: string;
        Element: typeof Link;
        isHighlighted?: undefined;
      }
    | { Element: typeof Command.Item; onSelect: () => void; value: string; isHighlighted?: boolean }
    | { Element: typeof RadixDropdownMenu.Item }
    | {
        Element: typeof Select.Item;
        onClick: () => void;
        value: string;
        isHighlighted?: undefined;
      }
  );

type Props = MenuItemProps & {
  menuStyle: MenuStyle;
};

const MenuItem = React.forwardRef<HTMLDivElement | HTMLAnchorElement, Props>(
  (
    {
      label,
      icon,
      rightIcon,
      eyebrow,
      menuStyle,
      tooltipText,
      Element = 'div',
      smallClassName,
      valueClassName,
      condensed,
      visible = true,
      isLoading,
      disabled,
      ...props
    },
    forwardedRef
  ) => {
    const [hovered, setHovered] = useState(false);
    const wrapperClasses = classnames(
      'text-1 flex items-center gap-3 px-6 py-5 text-left text-black outline-none last:border-0 aria-[highlighted]:bg-gray-light',
      'focus-visible:bg-gray-light',
      "aria-[selected='true']:bg-gray-light",
      {
        'border-b border-gray-light sm:border-0 sm:py-3': !condensed,
        'hover:bg-gray-light': hovered,
        '!cursor-wait': !!isLoading,
        '!cursor-not-allowed': !isLoading && !!disabled,
        '!cursor-pointer': !isLoading && !disabled,
        'py-3': condensed,
        'sm:p-4': menuStyle === 'form-field',
        'font-medium': menuStyle === 'standalone'
        // 'bg-gray-light':
        //   isHighlighted &&
        //   (menuStyle !== 'standalone-dark' && menuStyle === 'standalone-dark-unsorted')
      }
    );

    const labelRef = useRef<HTMLSpanElement>(null);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    useEffect(() => {
      if (!labelRef.current) {
        return;
      }

      const { scrollWidth, offsetWidth } = labelRef.current;
      setTooltipOpen(hovered && scrollWidth > offsetWidth);
    }, [hovered]);

    const iconUrl = useMemo(() => (isExternalUrl(icon ?? '') ? icon : undefined), [icon]);
    const iconElement = useMemo(() => {
      if (icon) {
        if (isLoading) return <LoadingDots color="gray" dotSize="small" />;
        return <Icon size="medium" name={icon} url={iconUrl} />;
      }
    }, [icon, iconUrl, isLoading]);

    const onClick = useCallback(() => {
      if (!disabled && !isLoading && 'onClick' in props) props.onClick();
    }, [disabled, isLoading, props]);

    const elementClassName = useMemo(() => {
      return (props as { className?: string }).className;
    }, [props]);

    if (!visible) {
      return null;
    }

    const element = (
      <Element
        {...props}
        // @ts-expect-error type union error
        ref={forwardedRef}
        className={classnames(elementClassName, wrapperClasses)}
        disabled={!!disabled}
        onMouseEnter={() => {
          if (!disabled) setHovered(true);
        }}
        onMouseLeave={() => setHovered(false)}
        onClick={onClick}
      >
        {iconElement}
        <MenuText
          smallClassName={classnames('text-2 font-normal text-gray-dark', smallClassName)}
          value={label}
          eyebrow={eyebrow}
          valueClassName={classnames('w-full truncate', valueClassName)}
          ref={labelRef}
        />
        {rightIcon && <Icon size="medium" className="ml-4" name={rightIcon} />}
      </Element>
    );

    if (!tooltipText) {
      return element;
    }

    return (
      <SimpleTooltip open={tooltipOpen}>
        <SimpleTooltip.Trigger asChild>{element}</SimpleTooltip.Trigger>
        <SimpleTooltip.Content className="max-w-80 px-2 py-3" side="right" sideOffset={10}>
          <SimpleTooltip.Arrow
            className="-my-px border-none fill-white drop-shadow-[0_1px_0_silver]"
            height={6}
            width={12}
          />
          {tooltipText}
        </SimpleTooltip.Content>
      </SimpleTooltip>
    );
  }
);

MenuItem.displayName = 'MenuItem';
export default MenuItem;
